// <s-product-videos> (snippets/product-videos.liquid): story-style video strip.
// - Previews ([data-video-preview], muted loops) load and play only while the
//   strip is on screen, and stay as posters with prefers-reduced-motion.
// - [data-video-open] opens the dialog player at that video (with sound when the
//   browser allows), [data-video-prev]/[data-video-next], arrow keys and the tap
//   zones move between videos, a finished video advances to the next one, and
//   [data-video-bar] shows each video's progress.
// - [data-video-buy] closes the player and scrolls to the product form.
class SProductVideos extends HTMLElement {
  #ac;
  #observer;
  #index = 0;
  #muted = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.addEventListener('click', (event) => this.#onClick(event), { signal });

    const player = this.#player();
    player?.addEventListener('ended', () => this.#go(1, { wrap: false }), { signal });
    player?.addEventListener('timeupdate', () => this.#updateProgress(), { signal });
    player?.addEventListener('click', () => {
      if (player.paused) player.play().catch(() => {});
      else player.pause();
    }, { signal });

    const dialog = this.#dialog();
    dialog?.addEventListener('close', () => this.#stop(), { signal });
    dialog?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.#go(1);
      if (event.key === 'ArrowLeft') this.#go(-1);
    }, { signal });

    this.#setupPreviews();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #dialog() {
    return this.querySelector('dialog');
  }

  #player() {
    return this.querySelector('[data-video-player]');
  }

  #items() {
    return [...this.querySelectorAll('[data-video-open]')];
  }

  #previews() {
    return [...this.querySelectorAll('[data-video-preview]')];
  }

  #setupPreviews() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!('IntersectionObserver' in window)) return;
    this.#observer = new IntersectionObserver((entries) => {
      const visible = entries.some((entry) => entry.isIntersecting);
      for (const video of this.#previews()) {
        if (visible) {
          if (!video.src && video.dataset.src) video.src = video.dataset.src;
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      }
    }, { rootMargin: '100px' });
    this.#observer.observe(this);
  }

  #onClick(event) {
    const open = event.target.closest('[data-video-open]');
    if (open) {
      this.#open(Number(open.dataset.index) || 0);
      return;
    }
    if (event.target.closest('[data-video-prev]')) this.#go(-1);
    else if (event.target.closest('[data-video-next]')) this.#go(1);
    else if (event.target.closest('[data-video-close]')) this.#dialog()?.close();
    else if (event.target.closest('[data-video-mute]')) this.#setMuted(!this.#muted);
    else if (event.target.closest('[data-video-buy]')) {
      // Reels outside a product page (Sign Kids home) carry their product's URL: go there.
      const productUrl = this.#items()[this.#index]?.dataset.productUrl;
      if (productUrl) {
        window.location.href = productUrl;
        return;
      }
      this.#dialog()?.close();
      document.querySelector('s-product-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  #open(index) {
    const dialog = this.#dialog();
    if (!dialog) return;
    for (const video of this.#previews()) video.pause();
    if (!dialog.open) dialog.showModal();
    this.#load(index);
  }

  #go(step, { wrap = true } = {}) {
    const count = this.#items().length;
    if (!count) return;
    let next = this.#index + step;
    if (next >= count) {
      if (!wrap) {
        // Last video finished: replay it instead of closing.
        this.#load(this.#index);
        return;
      }
      next = 0;
    }
    if (next < 0) next = count - 1;
    this.#load(next);
  }

  #load(index) {
    const item = this.#items()[index];
    const player = this.#player();
    if (!item || !player) return;
    this.#index = index;
    const items = this.#items();
    for (const side of this.querySelectorAll('[data-video-neighbor]')) {
      side.hidden = items.length < 2;
      if (items.length < 2) continue;
      const step = side.hasAttribute('data-video-prev') ? -1 : 1;
      const neighbor = items[(index + step + items.length) % items.length];
      const poster = side.querySelector('[data-neighbor-poster]');
      if (poster) poster.src = neighbor.dataset.poster || '';
    }
    this.#fillCard(item);
    player.poster = item.dataset.poster || '';
    player.src = item.dataset.full || '';
    player.muted = this.#muted;
    this.#updateProgress();
    player.play().catch(() => {
      // Browsers can block sound until the first tap: start muted instead.
      this.#setMuted(true);
      player.play().catch(() => {});
    });
  }

  // Per-reel product card ([data-video-card]); product pages use a fixed card and skip this.
  #fillCard(item) {
    const card = this.querySelector('[data-video-card]');
    if (!card) return;
    const { productTitle, productPrice, productImage } = item.dataset;
    card.hidden = !productTitle;
    if (!productTitle) return;
    const image = card.querySelector('[data-video-card-image]');
    if (image) {
      image.src = productImage || '';
      image.hidden = !productImage;
    }
    const title = card.querySelector('[data-video-card-title]');
    if (title) title.textContent = productTitle;
    const price = card.querySelector('[data-video-card-price]');
    if (price) price.textContent = productPrice || '';
  }

  #setMuted(muted) {
    this.#muted = muted;
    const player = this.#player();
    if (player) player.muted = muted;
    const button = this.querySelector('[data-video-mute]');
    button?.setAttribute('aria-pressed', String(muted));
    const sound = button?.querySelector('[data-icon-sound]');
    const mutedIcon = button?.querySelector('[data-icon-muted]');
    if (sound) sound.hidden = muted;
    if (mutedIcon) mutedIcon.hidden = !muted;
  }

  #updateProgress() {
    const player = this.#player();
    const ratio = player && player.duration ? player.currentTime / player.duration : 0;
    this.querySelectorAll('[data-video-bar]').forEach((bar, i) => {
      let width = 0;
      if (i < this.#index) width = 100;
      else if (i === this.#index) width = Math.min(100, ratio * 100);
      bar.style.width = `${width}%`;
    });
  }

  #stop() {
    const player = this.#player();
    if (player) {
      player.pause();
      player.removeAttribute('src');
      player.load();
    }
    if (this.#observer) {
      for (const video of this.#previews()) if (video.src) video.play().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-videos')) customElements.define('s-product-videos', SProductVideos);
